import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';

import { Organisation, User, UserGroup } from '@core/api';
import { APP_CONFIG, AppConfig } from '@core/config';
import { organisationSelectors, TokenService, userSelectors } from '@core/store';
import { SharedButtonsModule } from '@shared/buttons';

@Component({
  selector: 'la-contextual-header',
  styleUrls: ['./contextual-header.component.scss'],
  templateUrl: './contextual-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule, MatIconModule, MatMenuModule, MatTooltipModule, MatButtonModule, SharedButtonsModule],
})
export class ContextualHeaderComponent implements OnInit {
  private store = inject(Store);
  private tokenService = inject(TokenService);
  private cd = inject(ChangeDetectorRef);
  private router = inject(Router);

  @HostBinding('class.no-border') @Input({ transform: booleanAttribute }) noBorder = false;
  @Input() logoNavigates = true;
  @Output() navigate = new EventEmitter<MouseEvent>();
  @Output() openBuyMoreOsCredits = new EventEmitter();

  @HostBinding('class.middle') @Input() middle = false;

  user?: User;
  creditsBalance?: string;
  organization?: Organisation;
  isNatureReportingEnabled?: boolean;

  private config = inject<AppConfig>(APP_CONFIG);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.store
      .select(userSelectors.getUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.user = user;
        this.cd.markForCheck();
      });

    this.store
      .select(organisationSelectors.getOrganisation)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((organization) => {
        this.organization = organization;
        this.cd.markForCheck();
      });

    this.store
      .select(organisationSelectors.getUserHasNatureReportingAccess)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((hasAccess) => {
        this.isNatureReportingEnabled = hasAccess;
        this.cd.markForCheck();
      });
  }

  get logoUrl() {
    return this.organization?.logoUrl ? this.getOrganisationLogoUrl(this.organization?.logoUrl) : 'assets/layout/short-logo-green.svg';
  }

  get hasOrganization() {
    return !!this.organization;
  }

  get isAdmin() {
    return this.user?.groups?.includes(UserGroup.ADMIN);
  }

  openTopUpDialog() {
    this.openBuyMoreOsCredits.emit();
  }

  storeLastLocation() {
    this.navigate.emit();
  }

  openPrints() {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/prints']));
    window.open(url, '_blank', 'noopener');
  }

  openDataPurchases() {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/data-purchases']));
    window.open(url, '_blank', 'noopener');
  }

  openTitlePurchases() {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/title-purchases']));
    window.open(url, '_blank', 'noopener');
  }

  openPortfolio() {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/reports']));
    window.open(url, '_blank', 'noopener');
  }

  openNatureReporting() {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/nature-reporting']));
    window.open(url, '_blank', 'noopener');
  }

  openHelp() {
    window.open('https://help.thelandapp.com/', '_blank', 'noopener');
  }

  private getOrganisationLogoUrl(storage: string): string {
    if (!storage) {
      return '';
    }

    return `${this.config.api.mediaUrl}/prints/logo/${storage}?access_token=${this.tokenService.token?.access_token}`;
  }
}
